import { ACHIEVEMENTS } from '../games/nganya/config/achievements';
import {
  DEFAULT_VEHICLE_ID,
  getVehicle,
  LEGACY_SKIN_MAP,
  VEHICLES,
} from '../games/nganya/config/vehicles';
import type { PlayerProgress, RunResult } from '../games/nganya/types';
import { saveJson } from './localStorage';

const KEY = 'playhub.nganya.progress.v1';
const LEGACY_KEY = 'playhub.matatu-mayhem.progress.v1';

const DEFAULT_PROGRESS: PlayerProgress = {
  bestScore: 0,
  totalRuns: 0,
  totalFares: 0,
  totalDistance: 0,
  unlockedSkins: [DEFAULT_VEHICLE_ID],
  selectedSkin: DEFAULT_VEHICLE_ID,
  achievements: [],
  soundEnabled: true,
  musicEnabled: true,
  reducedEffects: false,
  lastDailyDate: null,
  lastDailyScore: 0,
};

function migrate(progress: PlayerProgress): PlayerProgress {
  const mapId = (id: string) => LEGACY_SKIN_MAP[id] ?? id;
  progress.selectedSkin = getVehicle(progress.selectedSkin).id;
  progress.unlockedSkins = [
    ...new Set(progress.unlockedSkins.map(mapId).concat(DEFAULT_VEHICLE_ID)),
  ];
  const valid = new Set(VEHICLES.map((v) => v.id));
  progress.unlockedSkins = progress.unlockedSkins.filter((id) => valid.has(id));
  if (!valid.has(progress.selectedSkin)) progress.selectedSkin = DEFAULT_VEHICLE_ID;
  return progress;
}

/**
 * Local demo profile store.
 * Future Laravel: GET/POST /api/games/nganya/profile
 * Never treat this as authoritative for paid entitlements.
 */
export class LocalGameProfileService {
  get(): PlayerProgress {
    try {
      const raw = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_KEY);
      if (!raw) return { ...DEFAULT_PROGRESS };
      return migrate({ ...DEFAULT_PROGRESS, ...(JSON.parse(raw) as Partial<PlayerProgress>) });
    } catch {
      return { ...DEFAULT_PROGRESS };
    }
  }

  save(progress: PlayerProgress): void {
    saveJson(KEY, migrate(progress));
  }

  update(patch: Partial<PlayerProgress>): PlayerProgress {
    const next = migrate({ ...this.get(), ...patch });
    this.save(next);
    return next;
  }

  applyRun(result: RunResult): { progress: PlayerProgress; newAchievements: string[] } {
    const p = this.get();
    p.totalRuns += 1;
    p.totalFares += result.fares;
    p.totalDistance += result.distance;
    p.bestScore = Math.max(p.bestScore, result.score);
    if (result.isDaily) {
      p.lastDailyDate = result.dailySeed;
      p.lastDailyScore = result.score;
    }

    const unlocked = new Set(p.achievements);
    const newly: string[] = [];

    const grant = (id: string) => {
      if (!unlocked.has(id)) {
        unlocked.add(id);
        newly.push(id);
      }
    };

    if (p.totalRuns >= 1) grant('first_run');
    if (p.totalFares >= 50) grant('fare_collector');
    if (result.crashes === 0 && result.endedBy === 'timer') grant('road_survivor');
    if (result.comboMax >= 5) grant('combo_king');
    if (p.totalDistance >= 5000) grant('distance_driver');

    p.achievements = [...unlocked];

    for (const vehicle of VEHICLES) {
      if (vehicle.unlockedByDefault || vehicle.unlock === 'starter') {
        if (!p.unlockedSkins.includes(vehicle.id)) p.unlockedSkins.push(vehicle.id);
        continue;
      }
      if (unlocked.has(vehicle.unlock) && !p.unlockedSkins.includes(vehicle.id)) {
        p.unlockedSkins.push(vehicle.id);
      }
    }

    this.save(p);
    return { progress: p, newAchievements: newly };
  }

  selectSkin(skinId: string): PlayerProgress {
    const p = this.get();
    const vehicle = getVehicle(skinId);
    if (!p.unlockedSkins.includes(vehicle.id)) return p;
    p.selectedSkin = vehicle.id;
    this.save(p);
    return p;
  }

  achievementMeta(id: string) {
    return ACHIEVEMENTS.find((a) => a.id === id);
  }
}

export const gameProfileService = new LocalGameProfileService();
